import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { FaEdit, FaSave, FaTimes, FaChartLine, FaUsers, FaExchangeAlt, FaUniversity } from 'react-icons/fa';
import LoadingImpulse from './LoadingImpulse';
import { useTable } from 'react-table';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

const buildRange = (type, customStart = '', customEnd = '') => {
  const now = new Date();
  const eatOffset = 3 * 60 * 60 * 1000;
  const eatNow = new Date(now.getTime() + eatOffset);

  let start, end;
  const toUTC = (d) => new Date(d.getTime() - eatOffset);

  if (type === 'custom') {
    if (!customStart || !customEnd) {
      return buildRange('30days');
    } else {
      start = new Date(customStart);
      start.setHours(0, 0, 0, 0);
      end = new Date(customEnd);
      end.setHours(0, 0, 0, 0);
      end.setDate(end.getDate() + 1);
    }
  } else {
    switch (type) {
      case 'today':
        start = new Date(eatNow);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(end.getDate() + 1);
        break;
      case 'yesterday':
        start = new Date(eatNow);
        start.setDate(start.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(end.getDate() + 1);
        break;
      case '7days':
        end = new Date(eatNow);
        end.setHours(23, 59, 59, 999);
        start = new Date(end);
        start.setDate(start.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        break;
      case '30days':
        end = new Date(eatNow);
        end.setHours(23, 59, 59, 999);
        start = new Date(end);
        start.setDate(start.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        break;
      case '1year':
        end = new Date(eatNow);
        end.setHours(23, 59, 59, 999);
        start = new Date(end);
        start.setFullYear(start.getFullYear() - 1);
        start.setHours(0, 0, 0, 0);
        break;
      default:
        start = new Date(0);
        end = new Date();
    }
  }

  return { startUTC: toUTC(start), endUTC: toUTC(end) };
};

const Settings = () => {
  const [rates, setRates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loadingRates, setLoadingRates] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [filterType, setFilterType] = useState('30days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Chart States
  const [mpesaChart, setMpesaChart] = useState({ labels: [], datasets: [] });
  
  const [userChart, setUserChart] = useState({ labels: [], datasets: [] });
  const [mt5Chart, setMt5Chart] = useState({ labels: [], datasets: [] });
  const [bankChart, setBankChart] = useState({ labels: [], datasets: [] });

  const [currentPaybill, setCurrentPaybill] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [paybillError, setPaybillError] = useState('');

  const [mpesaData, setMpesaData] = useState([]);

  const [loading, setLoading] = useState({
    mpesa: false,
    users: false,
    mt5: false,
    bank: false,
  });

  const token = localStorage.getItem('token');



  const fetchCurrentPaybill = async () => {
  try {
    const res = await axios.get('https://sys.instant-m-transfer.com/api/admin/mpesa-config/current');
    setCurrentPaybill(res.data);
    setPaybillError('');
  } catch (err) {
    setPaybillError('Could not load current paybill');
    setCurrentPaybill(null);
  }
};

// Switch
  const switchPaybill = async (slug) => {
    if (switching) return;
    setSwitching(true);
    setPaybillError('');

    try {
      const res = await axios.post(
        'https://sys.instant-m-transfer.com/api/admin/mpesa-config/switch',
        { slug }
        // ← no headers here anymore
      );
      setCurrentPaybill(res.data.current);
      alert(res.data.message);
    } catch (err) {
      setPaybillError(err.response?.data?.message || 'Failed to switch');
    } finally {
      setSwitching(false);
    }
  };

  const startEdit = (rate) => {
    setEditingId(rate._id);
    setEditForm({ ...rate });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };



  // M-Pesa Volume
  const fetchMpesaVolume = async () => {
    setLoading((prev) => ({ ...prev, mpesa: true }));
    const { startUTC, endUTC } = buildRange(filterType, customStart, customEnd);

    try {
      const res = await axios.get(
        'https://sys.instant-m-transfer.com/api/admin/transactions/daily-volume',
        {
          params: {
            startDate: startUTC.toISOString().split('T')[0],
            endDate: endUTC.toISOString().split('T')[0],
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const raw = res.data.dailyVolume || [];

      // Convert KES → USD using your fixed rates
      const converted = raw.map((day) => ({
        date: day.date,
        totalDepositsUSD: (day.totalDeposits || 0) / 133,
        totalWithdrawalsUSD: (day.totalWithdrawals || 0) / 125,
        // Optional: keep original KES values if you ever need them
        totalDepositsKES: day.totalDeposits || 0,
        totalWithdrawalsKES: day.totalWithdrawals || 0,
      }));
      converted.sort((a, b) => new Date(b.date) - new Date(a.date)); 
      
      setMpesaData(converted);

      const labels = converted.map((d) => d.date);
      const depositsUSD = converted.map((d) => d.totalDepositsUSD);
      const withdrawalsUSD = converted.map((d) => d.totalWithdrawalsUSD);

      setMpesaChart({
        labels,
        datasets: [
          {
            label: 'Deposits (USD)',
            data: depositsUSD,
            borderColor: 'rgb(34, 197, 94)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            tension: 0.3,
            fill: true,
          },
          {
            label: 'Withdrawals (USD)',
            data: withdrawalsUSD,
            borderColor: 'rgb(239, 68, 68)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            tension: 0.3,
            fill: true,
          },
        ],
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, mpesa: false }));
    }
  };

  // User Registrations
  const fetchUserRegistrations = async () => {
    setLoading((prev) => ({ ...prev, users: true }));
    const { startUTC, endUTC } = buildRange(filterType, customStart, customEnd);
    const startDateStr = startUTC.toISOString().split('T')[0];
    const endDateStr = endUTC.toISOString().split('T')[0];
    try {
      const res = await axios.get(
        'https://sys.instant-m-transfer.com/api/admin/users',
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const users = res.data.users || [];
      const grouped = {};

      users.forEach((user) => {
        if (!user.createdAt) return;
        const date = new Date(user.createdAt);
        const eatDate = new Date(date.getTime() + 3 * 60 * 60 * 1000);
        const key = eatDate.toISOString().split('T')[0];
        if (key >= startDateStr && key < endDateStr) {
          grouped[key] = (grouped[key] || 0) + 1;
        }
      });

      const sorted = Object.keys(grouped).sort();
      setUserChart({
        labels: sorted,
        datasets: [{
          label: 'New Users',
          data: sorted.map((d) => grouped[d]),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(99, 102, 241, 0.15)',
          tension: 0.4,
          fill: true,
        }]
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  };

  // MT5 Volume
  const fetchMT5Volume = async () => {
    setLoading((prev) => ({ ...prev, mt5: true }));
    const { startUTC, endUTC } = buildRange(filterType, customStart, customEnd);
    const startDateStr = startUTC.toISOString().split('T')[0];
    const endDateStr = endUTC.toISOString().split('T')[0];
    try {
      const res = await axios.get(
        'https://sys.instant-m-transfer.com/api/admin/mt5-transactions',
        { params: { limit: 10000 }, headers: { Authorization: `Bearer ${token}` } }
      );

      const txs = res.data.transactions || [];
      const grouped = {};

      txs.forEach((tx) => {
        if (!tx.transactionDate) return;
        const date = new Date(tx.transactionDate);
        const eatDate = new Date(date.getTime() + 3 * 60 * 60 * 1000);
        const key = eatDate.toISOString().split('T')[0];
        if (key >= startDateStr && key < endDateStr) {
          grouped[key] = (grouped[key] || 0) + (parseFloat(tx.amountUSD) || 0);
        }
      });

      const sorted = Object.keys(grouped).sort();
      setMt5Chart({
        labels: sorted,
        datasets: [{
          label: 'MT5 Volume (USD)',
          data: sorted.map((d) => grouped[d]),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(168, 85, 247, 0.15)',
          tension: 0.4,
          fill: true,
        }]
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, mt5: false }));
    }
  };

  // Bank Withdrawals
  const fetchBankVolume = async () => {
    setLoading((prev) => ({ ...prev, bank: true }));
    const { startUTC, endUTC } = buildRange(filterType, customStart, customEnd);
    const startDateStr = startUTC.toISOString().split('T')[0];
    const endDateStr = endUTC.toISOString().split('T')[0];
    try {
      const res = await axios.get(
        'https://sys.instant-m-transfer.com/api/admin/bank-transactions',
        { params: { limit: 10000 }, headers: { Authorization: `Bearer ${token}` } }
      );

      const txs = res.data.transactions || [];
      const grouped = {};

      txs.forEach((tx) => {
        if (!tx.transactionDate) return;
        const date = new Date(tx.transactionDate);
        const eatDate = new Date(date.getTime() + 3 * 60 * 60 * 1000);
        const key = eatDate.toISOString().split('T')[0];
        if (key >= startDateStr && key < endDateStr) {
          grouped[key] = (grouped[key] || 0) + (tx.amountKES || 0);
        }
      });

      const sorted = Object.keys(grouped).sort();
      setBankChart({
        labels: sorted,
        datasets: [{
          label: 'Bank Withdrawals (KES)',
          data: sorted.map((d) => grouped[d]),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(251, 146, 60, 0.15)',
          tension: 0.4,
          fill: true,
        }]
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, bank: false }));
    }
  };

  // Load all charts when filter changes
  useEffect(() => {
    const loadCharts = async () => {
      await Promise.all([
        fetchMpesaVolume(),
        fetchUserRegistrations(),
        fetchMT5Volume(),
        fetchBankVolume(),
        fetchCurrentPaybill(),
      ]);
    };
    loadCharts();
  }, [filterType, customStart, customEnd]);



  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: filterType === 'today' ? 'hour' : 'day',
          displayFormats: { hour: 'HH:mm', day: 'MMM dd' },
        },
      },
      y: { beginAtZero: true },
    },
  };

  const timeFilters = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: '7days', label: '7 days' },
    { key: '30days', label: '30 days' },
    { key: '1year', label: '1 year' },
  ];

  const ChartSection = ({ title, icon: Icon, loading: isLoading, data }) => (
    <section className="bg-white rounded-xl shadow-sm p-3 sm:p-4 lg:p-5 mx-2 sm:mx-0">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-2">
          {Icon && <Icon className="text-blue-600" />}
          {title}
        </h2>
      </div>
      <div className="relative h-64 sm:h-80 lg:h-96">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <LoadingImpulse size="lg" color="blue" />
          </div>
        ) : data.labels.length === 0 ? (
          <p className="text-center text-gray-500 pt-20">No data available</p>
        ) : (
          <Line data={data} options={chartOptions} />
        )}
      </div>
    </section>
  );

  const TableSection = ({ title, data }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Calculate pagination values
  const totalPages = Math.ceil(data.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = data.slice(startIndex, endIndex);

  const handleDownload = () => {
    const csv = ['Date,Deposits (USD),Withdrawals (USD)'];
    data.forEach((d) => {
      csv.push(
        `${d.date},${d.totalDepositsUSD.toFixed(2)},${d.totalWithdrawalsUSD.toFixed(2)}`
      );
    });
    const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'daily_mpesa_transaction_volume_usd.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <section className="bg-white rounded-xl shadow-sm p-3 sm:p-4 lg:p-5 mx-2 sm:mx-0">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base sm:text-lg font-semibold text-gray-800">{title}</h2>
        <button
          onClick={handleDownload}
          className="px-4 py-2 rounded-lg text-sm font-medium transition bg-blue-600 text-white hover:bg-blue-700"
        >
          Download CSV (USD)
        </button>
      </div>

      {data.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No data available</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[320px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className=" text-left p-3">Date</th>
                  <th className="text-right p-3">Deposits (USD)</th>
                  <th className="text-right p-3">Withdrawals (USD)</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {currentData.map((d) => (
                  <tr key={d.date} className="hover:bg-gray-50">
                    <td className="p-3">{d.date}</td>
                    <td className="p-3 text-right font-medium">
                      ${d.totalDepositsUSD.toFixed(2)}
                    </td>
                    <td className="p-3 text-right font-medium">
                      ${d.totalWithdrawalsUSD.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
          <div className="mt-2 flex flex-col sm:flex-row justify-between items-center gap-1 sm:mt-3 sm:gap-2 md:mt-4 md:gap-3">
          <button
            onClick={() => goToPage(currentPage - 1)}
            //disabled={!canPreviousPage}
            className="px-2 py-0.5 bg-gray-500 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200 text-xs font-medium sm:px-3 sm:py-1 sm:text-sm md:px-4 md:py-2 md:text-base font-['Open_Sans']"
          >
            Previous
          </button>
          <button
            onClick={() => goToPage(currentPage + 1)}
            //disabled={!canNextPage}
            className="px-2 py-0.5 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200 text-xs font-medium sm:px-3 sm:py-1 sm:text-sm md:px-4 md:py-2 md:text-base font-['Open_Sans']"
          >
            Next
          </button>
        </div>
          )}
        </>
      )}
    </section>
  );
};
  
  return (
    <div className="min-h-screen bg-gray-50 py-3 px-2 sm:px-4 lg:px-6">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <header className="flex items-center gap-2">
          <FaChartLine className="text-blue-600 text-lg sm:text-xl" />
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800">Settings & Analytics</h1>
        </header>

        {/* Error */}
        {error && (
          <div className="mx-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Time Filter */}
        <div className="flex gap-2 flex-wrap px-2 items-center">
          {timeFilters.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => {
                setFilterType(key);
                setCustomStart('');
                setCustomEnd('');
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filterType === key && !customStart && !customEnd
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
          <input
            type="date"
            value={customStart}
            onChange={(e) => {
              setCustomStart(e.target.value);
              setFilterType('custom');
            }}
            className="px-2 py-1 border rounded text-sm"
          />
          <span className="text-sm text-gray-700">to</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => {
              setCustomEnd(e.target.value);
              setFilterType('custom');
            }}
            className="px-2 py-1 border rounded text-sm"
          />
        </div>

        
        
        <section className="bg-white rounded-xl shadow-sm p-4 sm:p-5 lg:p-6 mx-2 sm:mx-0">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <FaUniversity className="text-blue-600" />
            Active M-Pesa Paybill (STK Deposit)
          </h2>

          {paybillError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {paybillError}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-5">
            <div className="flex-1">
              {currentPaybill ? (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="font-medium text-gray-800">
                    {currentPaybill.name}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Shortcode: <span className="font-mono">{currentPaybill.shortcode}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Environment: {currentPaybill.env}
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg text-gray-500 italic">
                  Loading current paybill...
                </div>
              )}
            </div>

            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => switchPaybill('jumis')}
                disabled={switching || currentPaybill?.slug === 'jumis'}
                className={`px-5 py-2.5 rounded-lg font-medium transition text-sm
                  ${currentPaybill?.slug === 'jumis'
                    ? 'bg-green-600 text-white cursor-default'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  } disabled:opacity-50`}
              >
                Use Jumis (Default)
              </button>

              <button
                onClick={() => switchPaybill('miki')}
                disabled={switching || currentPaybill?.slug === 'miki'}
                className={`px-5 py-2.5 rounded-lg font-medium transition text-sm
                  ${currentPaybill?.slug === 'miki'
                    ? 'bg-blue-900 text-white cursor-default'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  } disabled:opacity-50`}
              >
                Use Miki
              </button>

              <button
                onClick={fetchCurrentPaybill}
                disabled={switching}
                className="px-4 py-2.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition disabled:opacity-50 text-sm font-medium"
              >
                Refresh
              </button>
            </div>
          </div>

        </section>

        {/* Charts - Vertical Stack */}
        <ChartSection title="M-Pesa Transaction Volume (USD)" icon={FaExchangeAlt} loading={loading.mpesa} data={mpesaChart} />
        {/*<TableSection title="Daily M-Pesa Transaction Volume (USD)" data={mpesaData} />*/}
        <TableSection title="Daily Transaction Volume" data={mpesaData} />
        <ChartSection title="New User Registrations" icon={FaUsers} loading={loading.users} data={userChart} />
        <ChartSection title="MT5 Trading Volume (USD)" icon={FaChartLine} loading={loading.mt5} data={mt5Chart} />
        <ChartSection title="Bank Withdrawals (Paybill)" icon={FaUniversity} loading={loading.bank} data={bankChart} />

      </div>
    </div>
  );
};

export default Settings;